import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, ZoomIn, ZoomOut, Maximize2, Minimize2, List, X, Search, Moon, Sun, Bookmark, BookmarkCheck, Share2, Copy, Check, Download } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Set worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

export default function PDFViewer({ url, title, initialPage = 1, onError, onDownload }) {
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pageRefs = useRef([]);
  const [pdf, setPdf] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [scale, setScale] = useState(1.2); // Start at 120% for better readability
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [readProgress, setReadProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [renderedPages, setRenderedPages] = useState({});
  
  // Features state
  const [darkMode, setDarkMode] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);

  // Load data from localStorage
  useEffect(() => {
    const savedBookmarks = localStorage.getItem(`pdf-bookmarks-${url}`);
    if (savedBookmarks) setBookmarks(JSON.parse(savedBookmarks));
    
    const savedDarkMode = localStorage.getItem('pdf-dark-mode');
    if (savedDarkMode) setDarkMode(JSON.parse(savedDarkMode));
  }, [url]);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem(`pdf-bookmarks-${url}`, JSON.stringify(bookmarks));
  }, [bookmarks, url]);

  useEffect(() => {
    localStorage.setItem('pdf-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const loadingTask = pdfjsLib.getDocument({
          url: url,
          cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
          cMapPacked: true,
        });
        
        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        pageRefs.current = new Array(pdfDoc.numPages).fill(null);
        setLoading(false);
        
        generateThumbnails(pdfDoc);
      } catch (err) {
        console.error('PDF load error:', err);
        setError(err.message);
        setLoading(false);
        if (onError) onError(err);
      }
    };
    
    if (url) loadPdf();
  }, [url, onError]);

  // Scroll to initial page
  useEffect(() => {
    if (pdf && initialPage > 1 && pageRefs.current[initialPage - 1]) {
      setTimeout(() => scrollToPage(initialPage), 500);
    }
  }, [pdf, initialPage]);

  // Generate thumbnails
  const generateThumbnails = async (pdfDoc) => {
    const thumbs = [];
    const thumbScale = 0.15;
    
    for (let i = 1; i <= Math.min(pdfDoc.numPages, 30); i++) {
      try {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: thumbScale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        thumbs.push({ pageNum: i, dataUrl: canvas.toDataURL() });
      } catch (e) {
        console.error(`Thumbnail error for page ${i}:`, e);
      }
    }
    setThumbnails(thumbs);
  };

  // Render page with text layer for selection
  const renderPage = useCallback(async (pageNum, pageContainer) => {
    if (!pdf || !pageContainer) return;
    
    const canvas = pageContainer.querySelector('canvas');
    const textLayerDiv = pageContainer.querySelector('.text-layer');
    if (!canvas || !textLayerDiv) return;
    
    // Get device pixel ratio for sharp rendering on high-DPI screens (mobile)
    const pixelRatio = window.devicePixelRatio || 1;
    
    const renderKey = `${pageNum}-${scale}-${darkMode}-${pixelRatio}`;
    if (canvas.dataset.renderKey === renderKey) return;
    
    try {
      const page = await pdf.getPage(pageNum);
      const context = canvas.getContext('2d');
      const containerWidth = scrollContainerRef.current?.clientWidth || 800;
      const originalViewport = page.getViewport({ scale: 1 });
      
      // Calculate base scale - use more space on mobile
      const baseScale = isMobile 
        ? Math.min((containerWidth - 20) / originalViewport.width, 2)
        : Math.min((containerWidth - 80) / originalViewport.width, 1.5);
      const finalScale = baseScale * scale;
      const viewport = page.getViewport({ scale: finalScale });
      
      // Set canvas size accounting for pixel ratio (prevents blur on mobile)
      const outputWidth = Math.floor(viewport.width * pixelRatio);
      const outputHeight = Math.floor(viewport.height * pixelRatio);
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      
      // Scale context for high-DPI rendering
      context.scale(pixelRatio, pixelRatio);
      
      // Render canvas with high quality
      await page.render({ 
        canvasContext: context, 
        viewport: viewport,
        intent: 'display'
      }).promise;
      
      // Apply dark mode filter to canvas
      if (darkMode) {
        context.globalCompositeOperation = 'difference';
        context.fillStyle = 'white';
        context.fillRect(0, 0, viewport.width, viewport.height);
        context.globalCompositeOperation = 'source-over';
      }
      
      // Clear and render text layer for selection
      textLayerDiv.innerHTML = '';
      textLayerDiv.style.width = `${viewport.width}px`;
      textLayerDiv.style.height = `${viewport.height}px`;
      
      const textContent = await page.getTextContent();
      
      // Render text items
      textContent.items.forEach((item) => {
        const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
        
        const span = document.createElement('span');
        span.textContent = item.str;
        span.style.position = 'absolute';
        span.style.left = `${tx[4]}px`;
        span.style.top = `${tx[5] - item.height * finalScale}px`;
        span.style.fontSize = `${item.height * finalScale}px`;
        span.style.fontFamily = item.fontName || 'sans-serif';
        span.style.transformOrigin = '0% 0%';
        
        // Scale text to match width
        if (item.width > 0) {
          const textWidth = item.str.length * (item.height * finalScale * 0.5);
          const actualWidth = item.width * finalScale;
          if (textWidth > 0) {
            span.style.transform = `scaleX(${actualWidth / textWidth})`;
          }
        }
        
        textLayerDiv.appendChild(span);
      });
      
      canvas.dataset.renderKey = renderKey;
      setRenderedPages(prev => ({ ...prev, [pageNum]: renderKey }));
    } catch (err) {
      console.error(`Render error for page ${pageNum}:`, err);
    }
  }, [pdf, scale, darkMode, isMobile]);

  // Handle scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !pageRefs.current.length || !pdf) return;
    
    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;
    
    let currentVisible = 1;
    let minDistanceToCenter = Infinity;
    
    pageRefs.current.forEach((ref, index) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        const pageCenter = rect.top + rect.height / 2;
        const distanceToCenter = Math.abs(pageCenter - containerCenter);
        
        if (distanceToCenter < minDistanceToCenter) {
          minDistanceToCenter = distanceToCenter;
          currentVisible = index + 1;
        }
        
        const isNearViewport = rect.top < containerRect.bottom + 2000 && rect.bottom > containerRect.top - 2000;
        if (isNearViewport) {
          renderPage(index + 1, ref);
        }
      }
    });
    
    setCurrentPage(currentVisible);
    setReadProgress(Math.round((currentVisible / totalPages) * 100));
  }, [totalPages, renderPage, pdf]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && pdf && totalPages > 0) {
      container.addEventListener('scroll', handleScroll);
      
      const renderInitialPages = async () => {
        for (let i = 0; i < Math.min(totalPages, 10); i++) {
          const ref = pageRefs.current[i];
          if (ref) {
            await renderPage(i + 1, ref);
          }
        }
        handleScroll();
      };
      
      setTimeout(renderInitialPages, 300);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, pdf, totalPages, renderPage]);

  // Search
  const searchPdf = async (query) => {
    if (!pdf || !query.trim()) { setSearchResults([]); return; }
    setSearching(true);
    const results = [];
    
    try {
      for (let i = 1; i <= totalPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const text = textContent.items.map(item => item.str).join(' ');
        const lowerText = text.toLowerCase();
        const lowerQuery = query.toLowerCase();
        let index = 0;
        
        while ((index = lowerText.indexOf(lowerQuery, index)) !== -1) {
          const start = Math.max(0, index - 30);
          const end = Math.min(text.length, index + query.length + 30);
          results.push({
            pageNum: i,
            context: (start > 0 ? '...' : '') + text.substring(start, end) + (end < text.length ? '...' : ''),
          });
          index += query.length;
        }
      }
    } catch (e) { console.error('Search error:', e); }
    
    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);
    if (results.length > 0) scrollToPage(results[0].pageNum);
    setSearching(false);
  };

  const scrollToPage = (pageNum) => {
    if (pageRefs.current[pageNum - 1]) {
      pageRefs.current[pageNum - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const nextSearchResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(newIndex);
    scrollToPage(searchResults[newIndex].pageNum);
  };

  const prevSearchResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = currentSearchIndex <= 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(newIndex);
    scrollToPage(searchResults[newIndex].pageNum);
  };

  // Bookmarks
  const toggleBookmark = () => {
    if (bookmarks.includes(currentPage)) {
      setBookmarks(bookmarks.filter(p => p !== currentPage));
    } else {
      setBookmarks([...bookmarks, currentPage].sort((a, b) => a - b));
    }
  };

  const isBookmarked = bookmarks.includes(currentPage);

  // Share
  const getShareableLink = () => {
    const baseUrl = window.location.href.split('&page=')[0].split('?')[0];
    const params = new URLSearchParams(window.location.search);
    params.set('page', currentPage.toString());
    return `${baseUrl}?${params.toString()}`;
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareableLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error('Copy failed:', e); }
  };

  // Zoom
  const zoomIn = () => setScale(s => Math.min(s + 0.25, 3));
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.5));

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showSearch && e.key !== 'Escape') return;
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
      if (e.key === 'Escape') {
        if (isFullscreen) toggleFullscreen();
        if (showSearch) setShowSearch(false);
        if (showShareModal) setShowShareModal(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'b' && !showSearch) toggleBookmark();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, showSearch, showShareModal, currentPage]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-zinc-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-zinc-900">
        <div className="text-center p-6">
          <p className="text-red-400 mb-4">Failed to load PDF</p>
          <p className="text-zinc-500 text-sm mb-4">{error}</p>
          <a href={url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-500">
            Open in New Tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative w-full h-full flex ${isFullscreen ? 'bg-black' : darkMode ? 'bg-zinc-950' : 'bg-zinc-900'}`}>
      {/* Sidebar */}
      {(showThumbnails || showBookmarks) && (
        <div className="w-56 bg-zinc-800 border-r border-zinc-700 overflow-y-auto shrink-0">
          <div className="p-2 border-b border-zinc-700 flex items-center gap-1 flex-wrap">
            <button onClick={() => { setShowThumbnails(true); setShowBookmarks(false); }}
              className={`px-2 py-1 text-xs rounded ${showThumbnails ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}>
              Pages
            </button>
            <button onClick={() => { setShowBookmarks(true); setShowThumbnails(false); }}
              className={`px-2 py-1 text-xs rounded ${showBookmarks ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}>
              Saved ({bookmarks.length})
            </button>
            <button onClick={() => { setShowThumbnails(false); setShowBookmarks(false); }} className="p-1 hover:bg-zinc-700 rounded ml-auto">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
          
          {showThumbnails && (
            <div className="p-2 space-y-2">
              {thumbnails.map((thumb) => (
                <button key={thumb.pageNum} onClick={() => scrollToPage(thumb.pageNum)}
                  className={`w-full p-1 rounded border-2 transition-colors relative ${currentPage === thumb.pageNum ? 'border-violet-500 bg-violet-500/10' : 'border-transparent hover:border-zinc-600'}`}>
                  <img src={thumb.dataUrl} alt={`Page ${thumb.pageNum}`} className={`w-full rounded ${darkMode ? 'invert' : ''}`} />
                  <span className="text-xs text-zinc-400 mt-1 block">Page {thumb.pageNum}</span>
                  {bookmarks.includes(thumb.pageNum) && <BookmarkCheck className="absolute top-2 right-2 w-4 h-4 text-amber-400" />}
                </button>
              ))}
            </div>
          )}
          
          {showBookmarks && (
            <div className="p-2">
              {bookmarks.length === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-4">No bookmarks. Press B to bookmark.</p>
              ) : (
                <div className="space-y-1">
                  {bookmarks.map((pageNum) => (
                    <button key={pageNum} onClick={() => scrollToPage(pageNum)}
                      className={`w-full px-3 py-2 text-left text-sm rounded flex items-center gap-2 ${currentPage === pageNum ? 'bg-violet-600 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}>
                      <BookmarkCheck className="w-4 h-4 text-amber-400" />
                      Page {pageNum}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Viewer */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Progress Bar */}
        <div className="h-1 bg-zinc-800 w-full">
          <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300" style={{ width: `${readProgress}%` }} />
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="px-3 py-2 bg-zinc-800 border-b border-zinc-700 flex items-center gap-2">
            <Search className="w-4 h-4 text-zinc-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') searchPdf(searchQuery); if (e.key === 'Escape') setShowSearch(false); }}
              placeholder="Search in document..." className="flex-1 bg-transparent border-none outline-none text-sm text-white placeholder-zinc-500" autoFocus />
            {searching && <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />}
            {searchResults.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-zinc-400">{currentSearchIndex + 1}/{searchResults.length}</span>
                <button onClick={prevSearchResult} className="p-1 hover:bg-zinc-700 rounded"><ChevronUp className="w-4 h-4 text-zinc-400" /></button>
                <button onClick={nextSearchResult} className="p-1 hover:bg-zinc-700 rounded"><ChevronDown className="w-4 h-4 text-zinc-400" /></button>
              </div>
            )}
            <button onClick={() => setShowSearch(false)} className="p-1 hover:bg-zinc-700 rounded"><X className="w-4 h-4 text-zinc-400" /></button>
          </div>
        )}

        {/* Top Controls */}
        <div className="flex items-center justify-between px-3 py-2 bg-zinc-800/80 backdrop-blur-sm border-b border-zinc-700">
          <div className="flex items-center gap-1">
            <button onClick={() => { setShowThumbnails(!showThumbnails && !showBookmarks); setShowBookmarks(false); }}
              className={`p-2 rounded-lg transition-colors ${showThumbnails ? 'bg-violet-600 text-white' : 'hover:bg-zinc-700 text-zinc-400'}`} title="Toggle Thumbnails">
              <List className="w-4 h-4" />
            </button>
            <button onClick={() => setShowSearch(!showSearch)}
              className={`p-2 rounded-lg transition-colors ${showSearch ? 'bg-violet-600 text-white' : 'hover:bg-zinc-700 text-zinc-400'}`} title="Search (Ctrl+F)">
              <Search className="w-4 h-4" />
            </button>
            <button onClick={toggleBookmark}
              className={`p-2 rounded-lg transition-colors ${isBookmarked ? 'bg-amber-600 text-white' : 'hover:bg-zinc-700 text-zinc-400'}`} title="Bookmark (B)">
              {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </button>
            
            {!isMobile && (
              <>
                <div className="h-5 w-px bg-zinc-700 mx-1" />
                <button onClick={zoomOut} className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400" title="Zoom Out (-)"><ZoomOut className="w-4 h-4" /></button>
                <span className="text-xs text-zinc-400 min-w-[50px] text-center">{Math.round(scale * 100)}%</span>
                <button onClick={zoomIn} className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400" title="Zoom In (+)"><ZoomIn className="w-4 h-4" /></button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-300">Page {currentPage} / {totalPages}</span>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-xs text-zinc-500 hidden sm:block">{readProgress}%</span>
            <button onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-amber-600 text-white' : 'hover:bg-zinc-700 text-zinc-400'}`} title="Dark Mode">
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={() => setShowShareModal(true)} className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400" title="Share Page Link">
              <Share2 className="w-4 h-4" />
            </button>
            {onDownload && (
              <button onClick={onDownload} className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400" title="Download PDF">
                <Download className="w-4 h-4" />
              </button>
            )}
            <button onClick={toggleFullscreen} className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400" title="Fullscreen">
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Scrollable PDF Container */}
        <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto overflow-x-hidden ${darkMode ? 'bg-zinc-950' : 'bg-zinc-800'}`} style={{ scrollBehavior: 'smooth' }}>
          <div className="flex flex-col items-center py-4 gap-4">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <div key={pageNum} ref={el => pageRefs.current[pageNum - 1] = el}
                className="relative bg-white shadow-xl"
                style={{ minHeight: '200px' }}>
                <canvas className="block" style={{ maxWidth: '100%' }} />
                {/* Text layer for selection - positioned over canvas */}
                <div 
                  className="text-layer absolute top-0 left-0 overflow-hidden"
                  style={{
                    color: 'transparent',
                    opacity: darkMode ? 0 : 1,
                    mixBlendMode: 'multiply',
                    userSelect: 'text',
                    cursor: 'text',
                  }}
                />
                {/* Page Number Badge */}
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded pointer-events-none">{pageNum}</div>
                {bookmarks.includes(pageNum) && (
                  <div className="absolute top-2 right-2 pointer-events-none"><BookmarkCheck className="w-5 h-5 text-amber-400 drop-shadow-lg" /></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Bottom Controls */}
        {isMobile && (
          <div className="flex items-center justify-center gap-3 py-3 bg-zinc-800/80 backdrop-blur-sm border-t border-zinc-700">
            <button onClick={zoomOut} className="p-3 bg-zinc-700 rounded-full text-zinc-300"><ZoomOut className="w-5 h-5" /></button>
            <button onClick={() => setDarkMode(!darkMode)}
              className={`p-3 rounded-full ${darkMode ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}>
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <span className="text-sm text-zinc-300 min-w-[50px] text-center">{currentPage}/{totalPages}</span>
            <button onClick={toggleBookmark}
              className={`p-3 rounded-full ${isBookmarked ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}>
              {isBookmarked ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
            </button>
            <button onClick={zoomIn} className="p-3 bg-zinc-700 rounded-full text-zinc-300"><ZoomIn className="w-5 h-5" /></button>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowShareModal(false)}>
          <div className="bg-zinc-800 rounded-xl p-5 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Share Page Link</h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 hover:bg-zinc-700 rounded"><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-3">Share a link that opens this PDF at page {currentPage}</p>
            <div className="flex gap-2">
              <input type="text" value={getShareableLink()} readOnly className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-300" />
              <button onClick={copyShareLink}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${copied ? 'bg-green-600 text-white' : 'bg-violet-600 text-white hover:bg-violet-500'}`}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
